import React, { useCallback, useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./ProfileSelect.sass";

function ProfileSelect({
  value,
  options,
  onChange,
  onOpen,
  onClose,
  className,
  placeholder,
  errorMsg,
  ...rest
}) {
  const selectRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (isOpen) onOpen();
    else onClose();
  }, [isOpen, onOpen, onClose]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        close();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [close]);

  const selectedOption = options.find((opt) => opt.value === value) || null;

  return (
    <div
      ref={selectRef}
      className={`profile-select 
        ${isOpen ? "profile-select--open" : ""} 
        ${className} 
        ${errorMsg ? "profile-select--error" : ""}
        ${selectedOption ? "" : "profile-select--empty"}
        `}
      {...rest}
    >
      {/* Selected field */}
      <div
        role="button"
        tabIndex={0}
        className="profile-select-selected"
        onClick={toggle}
        onKeyPress={(e) => e.key === "Enter" && toggle()}
      >
        <span className="profile-select-selected__text">
          {selectedOption ? selectedOption.label : placeholder}
        </span>

        <div className="profile-select-selected__icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="10"
            height="6"
            viewBox="0 0 10 6"
            fill="none"
          >
            <path
              d="M8.70703 5L4.70703 1L0.707031 5"
              stroke="#AEAEAE"
              strokeWidth="2"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <ul className="profile-select-dropdown">
          {options.map((option, i) => (
            <li
              key={i}
              tabIndex={0}
              role="option"
              aria-selected={value === option.value}
              className={`profile-select-dropdown__option 
                ${
                  value === option.value
                    ? "profile-select-dropdown__option--selected"
                    : ""
                }`}
              onClick={() => {
                onChange(option);
                close();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onChange(option);
                  close();
                }
              }}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}

      {/* Error */}
      {errorMsg && (
        <span className="profile-select__error profile-select__error--other">
          {errorMsg}
        </span>
      )}
    </div>
  );
}

// --- PropTypes --- //
ProfileSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ).isRequired,

  onChange: PropTypes.func.isRequired,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  errorMsg: PropTypes.string,
};

ProfileSelect.defaultProps = {
  value: null,
  className: "",
  errorMsg: "",
  placeholder: "Not Set",
  onOpen: () => {},
  onClose: () => {},
};

export default ProfileSelect;
